/* Strike Gently product.js — product form, variant picker, gallery, lightbox, recommendations,
   recently viewed, Klaviyo view tracking. Loaded only on product templates, after theme.js. */
(function () {
  'use strict';
  const SG = (window.SG = window.SG || {});
  const dom = SG.dom || {};
  const $ = dom.$ || ((sel, root) => (root || document).querySelector(sel));
  const $$ = dom.$$ || ((sel, root) => Array.from((root || document).querySelectorAll(sel)));
  const on = dom.on || ((el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts));
  const warn = dom.warn || (() => {});
  const reduceMotion = dom.reduceMotion || (() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const money = SG.formatMoney || ((cents) => '$' + ((Number(cents) || 0) / 100).toFixed(2));
  const strings = SG.strings || {};

  function productJson(section) {
    try { return JSON.parse(($('[data-product-json]', section) || $('[data-product-json]')).textContent); } catch (e) { return null; }
  }

  /* ---------- Product form + variants ---------- */
  function initProductForm(section) {
    const form = $('form[data-product-form]', section);
    if (!form) return;
    const product = productJson(section);
    const priceEl = $('[data-product-price]', section);
    const compareEl = $('[data-product-compare]', section);
    const addBtn = $('[data-add-to-cart]', form);
    const addText = $('[data-add-to-cart-text]', form) || addBtn;
    const qtyInput = $('[data-quantity]', form);

    /* Sold out + Klaviyo back-in-stock on: the Add to Cart button (pinned to the bottom on phones) becomes
       "Email me when restocked" and opens Klaviyo's form, instead of a dead grey "Sold Out" bar. Klaviyo injects
       its trigger into the form after load; until it's there the button stays "Sold Out". */
    const bisEnabled = form.dataset.bis === 'true';
    const bisTrigger = () => $('.klaviyo-bis-trigger', form) || $('.klaviyo-bis-trigger');
    let shown = null;
    function setBuyButton(variant) {
      shown = variant;
      if (!addBtn || !variant) return;
      const bis = !variant.available && bisEnabled && !!bisTrigger();
      if (bis) form.setAttribute('data-bis-cta', ''); else form.removeAttribute('data-bis-cta');
      addBtn.disabled = !variant.available && !bis;
      if (addText) addText.textContent = variant.available ? (strings.add || 'Add to Cart') : bis ? (form.dataset.bisText || 'Email me when restocked') : (strings.soldOut || 'Sold Out');
    }
    if (bisEnabled && window.MutationObserver) {
      const watch = new MutationObserver(() => { if (bisTrigger()) { watch.disconnect(); setBuyButton(shown); } });
      watch.observe(form, { childList: true, subtree: true });
      setTimeout(() => watch.disconnect(), 15000);
    }

    function currentVariantId() {
      const checked = $('input[name="id"]:checked', form);
      if (checked) return Number(checked.value);
      const sel = $('select[name="id"]', form) || $('input[name="id"]', form);
      return sel ? Number(sel.value) : null;
    }
    function variantById(id) { return product && product.variants.find((v) => v.id === id); }

    function render(variant, fromChange) {
      if (!variant) return;
      const onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
      if (priceEl) {
        priceEl.textContent = money(variant.price);
        priceEl.classList.toggle('product-price--sale', onSale);
      }
      if (compareEl) {
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = money(variant.compare_at_price);
      }
      setBuyButton(variant);
      // gallery: jump to the variant's image
      if (variant.featured_media && SG.gallery) SG.gallery.goToMedia(variant.featured_media.id);
      // url: only once the shopper changes the variant (keeps the canonical URL on load)
      if (fromChange) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* noop */ }
      }
      document.dispatchEvent(new CustomEvent('product:variant', { detail: { variant } }));
      // Skio's plan picker listens for this one
      document.dispatchEvent(new CustomEvent('variantChanged', { detail: { variantId: variant.id, variant } }));
    }

    on(form, 'change', (e) => {
      if (e.target.name === 'id') render(variantById(currentVariantId()), true);
    });

    // quantity stepper
    $$('[data-qty-step]', form).forEach((btn) => on(btn, 'click', () => {
      if (!qtyInput) return;
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + Number(btn.dataset.qtyStep));
    }));

    on(form, 'submit', async (e) => {
      if (form.hasAttribute('data-bis-cta')) { // sold out: open Klaviyo's back-in-stock form instead
        e.preventDefault();
        const t = bisTrigger();
        if (t) t.click();
        return;
      }
      if (!window.fetch || !SG.addToCart) return; // let the browser post
      if ($('input[type="file"][name^="properties["]', form)) return; // uploads need a native multipart post
      e.preventDefault();
      const id = currentVariantId();
      if (!id) return;
      const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      const item = { id, quantity: qty };
      const plan = $('[name="selling_plan"]', form);
      if (plan && !plan.disabled && /^\d+$/.test(plan.value)) item.selling_plan = Number(plan.value);
      const props = {};
      $$('[name^="properties["]', form).forEach((inp) => {
        if (inp.disabled) return;
        if ((inp.type === 'checkbox' || inp.type === 'radio') && !inp.checked) return;
        const m = inp.name.match(/^properties\[(.+)\]$/);
        if (m && inp.value !== '') props[m[1]] = inp.value;
      });
      if (Object.keys(props).length) item.properties = props;
      try { await SG.addToCart([item], addBtn, { source: 'product' }); } catch (err) { /* toast shown */ }
    });

    // initial state (gallery is ready, so ?variant= deep links land on the right image)
    render(variantById(currentVariantId()));
  }

  /* ---------- Gallery: scroll-snap track + nav + dots + thumbs ---------- */
  function initGallery(section) {
    const gallery = $('[data-gallery]', section);
    if (!gallery) return;
    const track = $('[data-gallery-track]', gallery);
    const slides = $$('.gallery__slide', track);
    const prev = $('[data-gallery-prev]', gallery);
    const next = $('[data-gallery-next]', gallery);
    const dots = $$('[data-gallery-dot]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    let index = 0;

    function goTo(i, behavior) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft, behavior: behavior || (reduceMotion() ? 'auto' : 'smooth') });
    }
    function setActive(i) {
      index = i;
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      thumbs.forEach((t, n) => { t.classList.toggle('is-active', n === i); t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
      const atStart = i === 0, atEnd = i === slides.length - 1;
      // never disable the button that has focus without moving focus first
      if (prev && atStart && document.activeElement === prev && next) next.focus();
      if (next && atEnd && document.activeElement === next && prev) prev.focus();
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      // pause other videos
      $$('video', track).forEach((v) => { if (!slides[i].contains(v)) v.pause(); });
    }
    on(prev, 'click', () => goTo(index - 1));
    on(next, 'click', () => goTo(index + 1));
    dots.forEach((d, n) => on(d, 'click', () => goTo(n)));
    thumbs.forEach((t, n) => on(t, 'click', () => goTo(n)));
    on(track, 'scroll', () => {
      const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (i !== index) setActive(i);
    }, { passive: true });
    on(track, 'keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
    });
    setActive(0);

    SG.gallery = {
      goToMedia(mediaId) {
        const i = slides.findIndex((s) => Number(s.dataset.mediaId) === Number(mediaId));
        if (i >= 0) goTo(i, 'auto');
      },
    };
    window.SGGallery = SG.gallery;
  }

  /* ---------- Recommendations (PDP) ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (box) => {
      if (!box.dataset.productId || box.dataset.loaded) return;
      box.dataset.loaded = '1';
      try {
        const url = (SG.routes && SG.routes.productRecommendations || '/recommendations/products') + '?section_id=' + box.dataset.sectionId + '&product_id=' + box.dataset.productId + '&limit=' + (box.dataset.limit || 10) + '&intent=' + (box.dataset.intent || 'related');
        const res = await fetch(url);
        if (!res.ok) throw new Error('recommendations ' + res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.querySelector('.recommendations__card')) { box.innerHTML = fresh.innerHTML; box.hidden = false; }
      } catch (e) { warn('recommendations', e); }
    });
  }

  /* ---------- Recently viewed (localStorage ids -> /search?q=id:… section render) ---------- */
  const RECENT_KEY = 'sg_recently_viewed';
  function readRecent() { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter(Number); } catch (e) { return []; } }
  function rememberProduct(id) {
    if (!id) return;
    try {
      const list = readRecent().filter((x) => x !== id);
      list.unshift(id);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 20)));
    } catch (e) { /* storage unavailable */ }
  }
  async function initRecentlyViewed(currentId) {
    const box = $('[data-recently-viewed]');
    if (!box || !SG.routes || !SG.routes.search) return;
    const limit = Number(box.dataset.limit) || 8;
    const ids = readRecent().filter((id) => id !== currentId).slice(0, limit);
    if (!ids.length) return;
    try {
      const q = ids.map((id) => 'id:' + id).join(' OR ');
      const url = SG.routes.search + '?q=' + encodeURIComponent(q) + '&type=product&options[prefix]=none&section_id=' + box.dataset.sectionId;
      const res = await fetch(url);
      if (!res.ok) throw new Error('recently viewed ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-recently-viewed]');
      const track = fresh && fresh.querySelector('.recently-viewed__track');
      if (!track || !track.children.length) return;
      // newest first, as stored
      const items = Array.from(track.children);
      items.sort((a, b) => ids.indexOf(Number(a.dataset.productId)) - ids.indexOf(Number(b.dataset.productId)));
      items.forEach((el) => track.appendChild(el));
      box.innerHTML = fresh.innerHTML;
      box.hidden = false;
    } catch (e) { warn('recently viewed', e); }
  }

  /* ---------- Lightbox: tap a gallery image for the full-size version ---------- */
  function initLightbox(section) {
    const buttons = $$('[data-gallery-zoom]', section);
    if (!buttons.length) return;
    let box = null, img = null, index = 0, lastFocus = null;
    const build = () => {
      box = document.createElement('div');
      box.className = 'lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', strings.zoom || 'Image');
      box.hidden = true;
      box.innerHTML = '<button type="button" class="lightbox__close" data-lightbox-close aria-label="' + (strings.close || 'Close') + '">&times;</button>'
        + (buttons.length > 1 ? '<button type="button" class="lightbox__nav lightbox__nav--prev" data-lightbox-prev aria-label="' + (strings.previous || 'Previous') + '">&#8249;</button><button type="button" class="lightbox__nav lightbox__nav--next" data-lightbox-next aria-label="' + (strings.next || 'Next') + '">&#8250;</button>' : '')
        + '<div class="lightbox__scroller" data-lightbox-scroller><img class="lightbox__img" alt=""></div>'
        + '<p class="lightbox__hint"></p>';
      document.body.appendChild(box);
      img = $('.lightbox__img', box);
      on($('[data-lightbox-close]', box), 'click', close);
      on($('[data-lightbox-prev]', box), 'click', () => show(index - 1));
      on($('[data-lightbox-next]', box), 'click', () => show(index + 1));
      on(box, 'click', (e) => { if (e.target === box || e.target.matches('[data-lightbox-scroller]')) close(); });
      on(img, 'click', () => box.classList.toggle('is-zoomed'));
      on(document, 'keydown', (e) => {
        if (box.hidden) return;
        if (e.key === 'Escape' && !SG.layers) close(); // otherwise the layer stack in theme.js closes it
        if (e.key === 'ArrowRight') show(index + 1);
        if (e.key === 'ArrowLeft') show(index - 1);
      });
    };
    const show = (i) => {
      if (!box) build();
      const wasOpen = !box.hidden;
      index = (i + buttons.length) % buttons.length;
      const btn = buttons[index];
      box.classList.remove('is-zoomed');
      img.src = btn.dataset.zoomSrc;
      img.alt = btn.dataset.zoomAlt || '';
      const hint = $('.lightbox__hint', box);
      if (hint) hint.textContent = buttons.length > 1 ? (index + 1) + ' / ' + buttons.length : '';
      box.hidden = false;
      document.body.classList.add('lightbox-open');
      if (!wasOpen) {
        if (SG.setInert) SG.setInert([box, $('#Toast')], true);
        if (SG.layers) SG.layers.push(close);
        $('[data-lightbox-close]', box).focus({ preventScroll: true });
      }
    };
    const close = () => {
      if (!box || box.hidden) return;
      box.hidden = true;
      box.classList.remove('is-zoomed');
      document.body.classList.remove('lightbox-open');
      if (SG.setInert) SG.setInert([box, $('#Toast')], false);
      if (SG.layers) SG.layers.remove(close);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };
    buttons.forEach((btn, i) => on(btn, 'click', () => { lastFocus = btn; show(i); }));
  }

  /* ---------- Klaviyo viewed product ---------- */
  function trackView(section) {
    const el = $('[data-klaviyo-item]', section) || $('[data-klaviyo-item]');
    if (!el) return;
    try {
      const item = JSON.parse(el.textContent);
      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Viewed Product', item]);
      window._learnq.push(['trackViewedItem', { Title: item.Name, ItemId: item.ProductID, Categories: item.Categories, ImageUrl: item.ImageURL, Url: item.URL, Metadata: { Brand: item.Brand, Price: item.Price, CompareAtPrice: item.CompareAtPrice } }]);
    } catch (e) { warn('klaviyo', e); }
  }

  /* ---------- Skio: make sure a plan is selected on subscription-only products ----------
     Skio's picker crashes in its own preselect path for this store's plan setup, leaving
     no plan selected (and Add to Cart failing on products that require a selling plan).
     Once the component has rendered, select the Subscription group (or the first one). */
  async function initSkio() {
    const picker = $('skio-plan-picker');
    if (!picker || !window.customElements) return;
    try {
      await customElements.whenDefined('skio-plan-picker');
      try { await picker.updateComplete; } catch (e) { /* the component's own error */ }
      await new Promise((r) => setTimeout(r, 50));
      const requires = picker.product && picker.product.requires_selling_plan;
      const groups = picker.availableSellingPlanGroups || [];
      if (picker.selectedSellingPlan || !requires || !groups.length || typeof picker.selectSellingPlanGroup !== 'function') return;
      const preferred = groups.find((g) => /subscription/i.test(g.name || '')) || groups[0];
      picker.selectSellingPlanGroup(preferred);
    } catch (e) { warn('skio', e); }
  }

  function initSection(section) {
    initGallery(section);
    initProductForm(section);
    initLightbox(section);
  }

  function boot() {
    const section = $('[data-product-section]') || document;
    initSection(section);
    initRecommendations();
    initSkio();
    trackView(section);
    const product = productJson(section);
    const currentId = product ? product.id : null;
    initRecentlyViewed(currentId);
    rememberProduct(currentId);
    // Shopify theme editor: a reloaded product section needs its form/gallery again
    on(document, 'shopify:section:load', (e) => {
      const root = e.target;
      if (root.querySelector('[data-product-section]')) initSection(root.querySelector('[data-product-section]'));
      initRecommendations();
    });
  }
  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', boot); else boot();
})();
